// Historie úkolů — přehled změn NAPŘÍČ všemi úkoly (Operations i trenérské).
// Role: JEN owner. Backend: /api/ukoly-historie (tabulka ukoly_historie v PG).
//
// K čemu to je: "kdo a kdy posunul deadline u téhle věci" se dřív nedalo dohledat —
// v DB zůstala jen poslední hodnota. Tady je vidět celá stopa: co se změnilo,
// z čeho na co, kdo to udělal a kdy.
//
// Pozn. k hookům: všechny useState/useMemo/useEffect jsou nahoře v komponentě,
// žádný pod podmíněným returnem (React #310).

import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight, History, RefreshCw, SlidersHorizontal, X } from "lucide-react";
import { Link } from "react-router-dom";
import GradientHeader from "../components/GradientHeader.jsx";
import { useApiData } from "../lib/api.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { Stranka, EmptyState, Hlaska, AkceZnovu, KartaSeznam, PruhFiltru, SkeletonRows, Stitek } from "../components/ui.jsx";
import { POLE_POPISKY, vetaZmeny, kdyKratce } from "../lib/ukolyHistorie.js";

const STRANKA = 100;

// "před 7 dny" jako výchozí okno — dohledává se typicky čerstvá změna.
function isoPredDny(dny) {
  const d = new Date();
  d.setDate(d.getDate() - dny);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TABULKA_POPISKY = { company: "Operations", trener: "Trenéři" };

// 4.9.2026: tóny se berou z jednotné sady, ne z vlastní tabulky v lib/ukolyHistorie.
const TON_POLE = {
  deadline: "pozor", status: "znacka", pilot: "info", priority: "problem",
  kategorie: "neutralni", nazev: "neutralni", popis: "neutralni", hotovo: "ok",
};

// Odkaz na konkrétní úkol na příslušném boardu (oba podporují deep-link).
function odkazNaUkol(radek) {
  return radek.tabulka === "trener"
    ? `/manager/treneri/ukoly?open=${encodeURIComponent(radek.ukol_id)}`
    : `/operations/ukoly?ukol=${encodeURIComponent(radek.ukol_id)}`;
}

export default function UkolyHistorie() {
  const [filtrKdo, setFiltrKdo] = useState("vse");
  const [filtrPole, setFiltrPole] = useState("vse");
  const [od, setOd] = useState(isoPredDny(30));
  const [doDne, setDoDne] = useState("");
  const [strana, setStrana] = useState(0);
  // 8.9.2026: na telefonu zabraly čtyři filtry pod sebou celou obrazovku, než se
  // člověk dostal k první změně. Schované za tlačítkem s počtem, na počítači
  // zůstávají rozbalené jako dřív.
  const uzka = useUzkaObrazovka();
  const [filtryOtevrene, setFiltryOtevrene] = useState(false);

  // Změna filtru vždy vrací na první stranu (jinak by uživatel koukal na prázdno).
  useEffect(() => { setStrana(0); }, [filtrKdo, filtrPole, od, doDne]);

  const dotaz = useMemo(() => {
    const p = new URLSearchParams();
    if (filtrKdo && filtrKdo !== "vse") p.set("kdo", filtrKdo);
    if (filtrPole && filtrPole !== "vse") p.set("pole", filtrPole);
    if (od) p.set("od", od);
    if (doDne) p.set("do", doDne);
    p.set("limit", String(STRANKA));
    p.set("offset", String(strana * STRANKA));
    return p.toString();
  }, [filtrKdo, filtrPole, od, doDne, strana]);

  const { data, loading, error, refetch, refreshing } = useApiData(`/api/ukoly-historie?${dotaz}`);

  const polozky = useMemo(() => (Array.isArray(data?.polozky) ? data.polozky : []), [data]);
  const autori = useMemo(() => (Array.isArray(data?.autori) ? data.autori : []), [data]);
  const maDalsi = data?.maDalsi === true;

  // Seskupení po dnech — dlouhý seznam se jinak čte hrozně.
  const poDnech = useMemo(() => {
    const mapa = new Map();
    for (const r of polozky) {
      const den = String(r.kdy || "").substring(0, 10) || "—";
      if (!mapa.has(den)) mapa.set(den, []);
      mapa.get(den).push(r);
    }
    return [...mapa.entries()];
  }, [polozky]);

  const pocetFiltru = (filtrKdo !== "vse" ? 1 : 0) + (filtrPole !== "vse" ? 1 : 0) + (od !== isoPredDny(30) ? 1 : 0) + (doDne ? 1 : 0);
  const filtrAktivni = pocetFiltru > 0;
  const zrusFiltry = () => { setFiltrKdo("vse"); setFiltrPole("vse"); setOd(isoPredDny(30)); setDoDne(""); };
  const vstupCls = "border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300";

  return (
    <Stranka>
      <GradientHeader
        title="Historie úkolů"
        icon={History}
        subtitle="Kdo a kdy posunul termín, přehodil pilota nebo přesunul úkol jinam."
        loading={refreshing}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing }]}
      />

      {/* Filtry: na počítači rozbalený pruh, na telefonu za tlačítkem s počtem. */}
      {uzka && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltryOtevrene((o) => !o)}
            className={`flex min-h-[44px] flex-1 items-center gap-1.5 rounded-xl border px-3 ${filtrAktivni || filtryOtevrene ? "border-2 border-brand bg-brand-50" : "border-slate-300 bg-white"}`}
          >
            <SlidersHorizontal className={`h-4 w-4 shrink-0 ${filtrAktivni || filtryOtevrene ? "text-brand-deep" : "text-slate-400"}`} />
            <span className={`text-sm font-bold ${filtrAktivni || filtryOtevrene ? "text-brand-deep" : "text-slate-700"}`}>Filtry</span>
            {pocetFiltru > 0 && (
              <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-white">{pocetFiltru}</span>
            )}
          </button>
          {filtrAktivni && (
            <button
              type="button"
              onClick={zrusFiltry}
              className="btn btn-secondary"
            >
              <X className="h-4 w-4" /> Zrušit vše
            </button>
          )}
        </div>
      )}
      {(!uzka || filtryOtevrene) && (
      <PruhFiltru
        zapnuto={!uzka && filtrAktivni}
        onZrusit={zrusFiltry}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <label className="min-w-0">
            <span className="label">Kdo změnu udělal</span>
            <select className="field" value={filtrKdo} onChange={(e) => setFiltrKdo(e.target.value)} aria-label="Kdo změnu udělal">
              <option value="vse">Kdokoli</option>
              {autori.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="label">Které pole</span>
            <select className="field" value={filtrPole} onChange={(e) => setFiltrPole(e.target.value)} aria-label="Které pole">
              <option value="vse">Všechna pole</option>
              {Object.entries(POLE_POPISKY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {/* Od a Do vedle sebe — dvě data pod sebou zabírala na telefonu dva řádky zbytečně. */}
          <div className="grid grid-cols-2 gap-2 sm:contents">
            <label className="min-w-0">
              <span className="label">Od</span>
              <input type="date" className="field" value={od} onChange={(e) => setOd(e.target.value)} />
            </label>
            <label className="min-w-0">
              <span className="label">Do</span>
              <input type="date" className="field" value={doDne} onChange={(e) => setDoDne(e.target.value)} />
            </label>
          </div>
        </div>
      </PruhFiltru>
      )}

      {error && (
        <Hlaska ton="problem" title="Historii se nepodařilo načíst" akce={<AkceZnovu onClick={refetch} />}>
          {String(error)}
        </Hlaska>
      )}

      {loading && polozky.length === 0 && (
        <div className="glass !p-0 overflow-hidden"><SkeletonRows rows={6} /></div>
      )}

      {!error && !loading && polozky.length === 0 && (
        <div className="glass">
          <EmptyState icon={History} title="Za zvolené období tu není žádná změna" hint="Zkus posunout datum od, nebo zrušit filtry." />
        </div>
      )}

      <div className="space-y-4">
        {poDnech.map(([den, radky]) => (
          <KartaSeznam
            key={den}
            title={den === "—" ? "Bez data" : new Date(den).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}
            odznak={radky.length}
          >
            <ul className="divide-y divide-slate-100">
              {radky.map((r) => (
                <li key={r.id} className="flex min-h-[64px] items-start gap-3 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-800 demo-sensitive">
                      <span className="font-semibold">{r.kdo || "Někdo"}</span> {vetaZmeny(r)}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Stitek velikost="mala" ton={TON_POLE[r.pole] || "neutralni"}>{POLE_POPISKY[r.pole] || r.pole}</Stitek>
                      <Stitek velikost="mala" ton="neutralni">{TABULKA_POPISKY[r.tabulka] || r.tabulka}</Stitek>
                      <Stitek velikost="mala" ton="neutralni">{kdyKratce(r.kdy)}</Stitek>
                      <span className="min-w-0 truncate text-[11px] font-semibold text-brand-deep demo-sensitive">{r.ukol_nazev || `Úkol #${r.ukol_id}`}</span>
                    </div>
                  </div>
                  <Link to={odkazNaUkol(r)} aria-label="Otevřít úkol" title="Otevřít úkol" className="btn-ikona shrink-0">
                    <ArrowUpRight className="h-5 w-5" />
                  </Link>
                </li>
              ))}
            </ul>
          </KartaSeznam>
        ))}
      </div>

      {/* Stránkování — bez COUNT(*), backend jen říká "je toho víc". */}
      {(strana > 0 || maDalsi) && (
        <div className="flex items-center justify-center gap-2">
          <button type="button" disabled={strana === 0} onClick={() => setStrana((s) => Math.max(0, s - 1))}
            className="btn btn-secondary btn-sm">
            <ChevronLeft className="h-4 w-4" /> Novější
          </button>
          <span className="text-xs text-slate-400 tabular-nums">strana {strana + 1}</span>
          <button type="button" disabled={!maDalsi} onClick={() => setStrana((s) => s + 1)}
            className="btn btn-secondary btn-sm">
            Starší <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </Stranka>
  );
}
